import { Dialog, DialogContent, DialogActions } from "@mui/material";
import { useCallback, useEffect, useRef } from "react";
import { ReportViewerResponse } from "../../config/hooks/utils/report_viewer";

import PrimaryButton from "./primary_button";
import ProgressBar from "./progress_bar";



function base64ToBlob(base64: string, contentType: string) {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length)
    .fill(0)
    .map((_, i) => byteCharacters.charCodeAt(i));
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: contentType });
}

/**
 * CustomModalLoading
 */

const CustomModalLoading = ({
  loading,
  message,
}: { loading: boolean, message?: string }) => {
  return (
    <>
      {
        loading &&
        <div className="absolute size-full z-50 top-0 left-0 flex justify-center items-center">
          <div className="flex flex-col w-48 p-4 rounded-lg gap-2 border border-stone-500 justify-center items-center bg-[#FFFFFF] dark:bg-[#393939]">
            <div className="w-24">
              <img src={`images/mini-logo.png`} alt="logo gdol" />
            </div>
            <div className="w-full">
              <ProgressBar />
              <p className="text-black dark:text-white text-center">{message ?? "Salvando"}</p>
            </div>
          </div>
        </div>
      }
    </>
  );
}

/**
 * ReportViewerModal
 */

type Props = {
  reportViewerState: ReportViewerResponse,
  cancelButtonLabel?: string;
  savingLabel?: string;
  buttonColor?: "primary-green" | "white" | "secondary" | "secondary-white" | "yellow" | "red";
  leftButtonSlot?: React.ReactNode,
};

export const ReportViewerModal = ({
  reportViewerState,
  cancelButtonLabel,
  savingLabel,
  buttonColor,
  leftButtonSlot,

}: Props) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (reportViewerState.reportData) {
      window.requestAnimationFrame(() => {
        if (iframeRef.current) {
          if (reportViewerState.reportData) {
            if (reportViewerState.reportData.mimeType === 'text/html') {
              const blob = base64ToBlob(reportViewerState.reportData.bytes, "text/html");
              const url = URL.createObjectURL(blob);
              iframeRef.current.src = url;
            }
          }
        }
      });
    }
  }, [reportViewerState.reportData]);

  const handleClose = useCallback(() => {
    if (!reportViewerState.saving) {
      reportViewerState.handleReportViewerClose();
    }
  }, [
    reportViewerState,
  ]);

  const handleDownloadType = useCallback((option: number) => {
    if (!reportViewerState.saving) {
      switch (option) {
        case 1:
          reportViewerState.handleReportViewerSubmit('application/pdf');
          break;
        case 2:
          reportViewerState.handleReportViewerSubmit('application/vnd.ms-excel');
          break;
        case 3:
          reportViewerState.handleReportViewerSubmit('application/vnd.ms-word');
          break;
      }
    }
  }, [
    reportViewerState
  ]);


  return (
    <Dialog
      open={reportViewerState.open}
      onClose={handleClose}
      fullScreen={true}
      sx={{
        "& .MuiDialog-container": {
          position: 'relative',

          "& .MuiPaper-root": {
            width: '95vw',
            height: '95vh',
          },
          "& .MuiFormControlLabel-label": {
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: '13px',
          },
          "& .MuiCheckbox-root": {
            padding: '.25rem',
          },
        },
      }}>

      <DialogContent>
        {
          reportViewerState.reportData && reportViewerState.reportData.bytes && reportViewerState.reportData.bytes.length > 0 ? (
            <>
              {
                reportViewerState.reportData.mimeType === 'text/html'
                  ? (
                    <iframe
                      ref={iframeRef}
                      className="w-full h-full bg-white"
                      title="report_viewer"
                    />
                  ) : (
                    <div className="size-full flex flex-col justify-center items-center">
                      <span className="text-lg font-bold">Este conteudo não pode ser pre-visualizado.</span>
                    </div>
                  )
              }
            </>
          ) : (
            <div className="w-full h-full flex flex-row justify-center items-center">
              <div className="text-black dark:text-white">
                Falha ao gerar relatorio.
              </div>
            </div>
          )
        }
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-between flex p-4 gap-4">
          <div className="flex flex-row">
            {leftButtonSlot}
          </div>

          <div className="flex flex-row gap-2 items-center">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={handleClose}>
                {cancelButtonLabel ?? 'Cancelar'}
              </h1>
            </div>

            <div>
              <div className="flex flex-row gap-2">
                <div className="w-[8rem]">
                  <PrimaryButton
                    disabled={reportViewerState.saving}
                    buttonColor={buttonColor ?? "primary-green"}
                    onClick={() => handleDownloadType(1)}>
                    Baixar PDF
                  </PrimaryButton>
                </div>

                <div className="w-[8rem]">
                  <PrimaryButton
                    disabled={reportViewerState.saving}
                    buttonColor={buttonColor ?? "primary-green"}
                    onClick={() => handleDownloadType(2)}>
                    Baixar Excel
                  </PrimaryButton>
                </div>

                <div className="w-[8rem]">
                  <PrimaryButton
                    disabled={reportViewerState.saving}
                    buttonColor={buttonColor ?? "primary-green"}
                    onClick={() => handleDownloadType(3)}>
                    Baixar Word
                  </PrimaryButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogActions>

      <CustomModalLoading
        loading={reportViewerState.saving}
        message={(savingLabel ?? 'Baixando...')} />

    </Dialog>
  );
}